import { forwardRef, useEffect, useRef } from 'react';
import { t } from 'i18next';
// configs
import { WPP_LINK_PLG, WPP_LINK_TRADICIONAL } from 'config/flags';
// components
import { Popover, PopoverProps } from 'rsuite';
import TimerWithProgress from '../TimerWithProgress';
// styles
import styles from './PopoverWahtsapp.module.css';

interface PopoverWhatsappProps extends Omit<PopoverProps, 'title' | 'children'> {
    quantity: number;
    action: () => void;
    user: any;
}

const PopoverWhatsapp = forwardRef<HTMLDivElement, PopoverWhatsappProps>(
    ({ quantity, action, user, ...props }, ref) => {
        const popoverRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            function handleClickOutside(event: MouseEvent) {
                if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                    action();
                }
            }

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        return (
            <div ref={popoverRef} className={styles['whisper-popover']} onMouseLeave={() => action()}>
                <Popover ref={ref} {...props}>
                    <div className={styles['wrapper']}>
                        <div className={styles['timer']}>
                            <TimerWithProgress
                                maxDays={30}
                                daysLeft={quantity}
                                width={56}
                                height={56}
                                withTextDay={false}
                                withPopover={false}
                                withStrongColor={true}
                                backgroundColor={'#fff'}
                            />
                        </div>
                        <div className={styles['wrapper-content']}>
                            <div>
                                <p className={styles['title']}>
                                    {quantity > 7
                                        ? t('home.timerTrial.alert.title.init')
                                        : quantity > 4
                                        ? t('home.timerTrial.alert.title.middle', { value: quantity })
                                        : quantity === 1
                                        ? t('home.timerTrial.alert.title.lastOneDay')
                                        : t('home.timerTrial.alert.title.lastDay', { value: quantity })}
                                </p>
                                <p className={styles['text']}>
                                    {quantity > 7
                                        ? t('home.timerTrial.alert.subtitle.init', { value: quantity })
                                        : quantity > 4
                                        ? t('home.timerTrial.alert.subTitle.middle')
                                        : t('home.timerTrial.alert.subTitle.lastDay')}
                                </p>
                            </div>
                            <a
                                href={`https://wa.me/${
                                    user?.client?.is_free_trial ? WPP_LINK_PLG : WPP_LINK_TRADICIONAL
                                }?text=${encodeURIComponent('INICIANDO CHAT CON EXTENDEAL')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => action && action()}
                                style={{ textDecoration: 'none' }}
                            >
                                <div className={styles['wrapper-whastsapp']}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M8.03353 0C3.64259 0 0.0684557 3.55648 0.0665929 7.9277C0.0661272 9.32499 0.433087 10.6889 1.13022 11.8911L0 16L4.2233 14.8975C5.38704 15.5291 6.69701 15.8619 8.03027 15.8624H8.03353C12.424 15.8624 15.9981 12.3054 16 7.93419C16.0009 5.81578 15.1729 3.82389 13.6688 2.32557C12.1646 0.826787 10.1645 0.000926892 8.03353 0ZM8.03353 14.5235H8.03074C6.84277 14.5235 5.67716 14.2051 4.66057 13.6049L4.41888 13.4622L1.91303 14.1166L2.58176 11.6849L2.42436 11.4355C1.76169 10.3863 1.41149 9.17344 1.41196 7.92863C1.41335 4.29522 4.38396 1.33936 8.03632 1.33936C9.805 1.33982 11.4675 2.02618 12.7174 3.27193C13.9678 4.51721 14.6556 6.1731 14.6551 7.93373C14.6537 11.5671 11.6831 14.5235 8.03353 14.5235ZM11.6654 9.58823C11.4666 9.48905 10.4877 9.00985 10.3051 8.94358C10.1226 8.8773 9.98987 8.8444 9.85715 9.04275C9.72443 9.24111 9.34304 9.68741 9.22708 9.81949C9.11112 9.95203 8.9947 9.96825 8.79586 9.86908C8.59701 9.7699 7.95529 9.56042 7.1953 8.88564C6.60341 8.36056 6.20385 7.71174 6.0879 7.51338C5.97194 7.31503 6.07533 7.20797 6.17545 7.10926C6.26486 7.02074 6.37429 6.878 6.47395 6.76214C6.57361 6.64628 6.60667 6.56378 6.6728 6.43124C6.73892 6.29915 6.70586 6.18329 6.65603 6.08412C6.6062 5.98494 6.20805 5.00985 6.04226 4.61314C5.88067 4.22709 5.71675 4.27899 5.59427 4.27297C5.47832 4.26694 5.3456 4.26602 5.21288 4.26602C5.08016 4.26602 4.86454 4.31561 4.682 4.51396C4.49945 4.71232 3.98533 5.19152 3.98533 6.16661C3.98533 7.1417 4.69876 8.08388 4.79795 8.21597C4.89761 8.34805 6.20152 10.3492 8.19838 11.207C8.67338 11.411 9.04407 11.5328 9.33326 11.6241C9.81012 11.7748 10.2441 11.7534 10.5869 11.7025C10.9692 11.6455 11.7646 11.2233 11.9304 10.7603C12.0962 10.2978 12.0962 9.90106 12.0463 9.8181C11.9965 9.73561 11.8638 9.68602 11.6649 9.58684L11.6654 9.58823Z"
                                            fill="#07AD4B"
                                        />
                                    </svg>
                                    <p className={styles['label']}>{t('home.timerTrial.text.wpp')}</p>
                                </div>
                            </a>
                        </div>
                    </div>
                </Popover>
            </div>
        );
    },
);

export default PopoverWhatsapp;
